// Add toast import
import { toast } from 'sonner'
import { fDate } from '@/utils/dayjs'
import { useState, useEffect } from 'react'
import { Label } from '@/components/ui/label'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { fCurrency } from '@/utils/format-number'
import { Textarea } from '@/components/ui/textarea'
import { getBankLabel } from '@/utils/common.utils'
import { Package, User, MapPin, CreditCard } from 'lucide-react'
import { getFirstImageMedia } from '@/utils/get-first-image-media'
import LightboxDialog from '@/components/share-components/lightbox-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

import { Order, ShippingStatus } from './types'
import { getStatusBadge, getShippingStatusBadge } from './utils'

interface OrderStatusModalProps {
  open: boolean
  onClose: () => void
  order: Order | null
  loading?: boolean
  onOrderUpdate?: (updatedOrder: Order) => void
}

export default function OrderStatusModal({
  open,
  onClose,
  order,
  onOrderUpdate,
}: OrderStatusModalProps) {
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  // Reset form when order changes
  useEffect(() => {
    if (order) {
      setNotes(order.shippingNote || '')
    }
  }, [order])

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setNotes('')
      setSubmitting(false)
    }
  }, [open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!order) return

    try {
      setSubmitting(true)

      const response = await axios.patch(
        endpoints.orders.confirmPayment(order.cartId),
        { note: notes },
      )

      // Update the order data in the table with the returned data
      if (onOrderUpdate && response.data) {
        onOrderUpdate(response.data)
      }

      toast.success('ยืนยันการชำระเงินสำเร็จ')
      onClose()
    } catch (error) {
      console.error('Failed to update shipping status:', error)
      toast.error('เกิดข้อผิดพลาดในการยืนยันการชำระเงิน')
    } finally {
      setSubmitting(false)
    }
  }

  if (!order) return null

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        aria-describedby=""
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            ข้อมูลออเดอร์ - แก้ไขสถานะออเดอร์
          </DialogTitle>
          <DialogDescription>
            ดูข้อมูลทั้งหมดของออเดอร์และอัปเดตสถานะการชำระเงิน
          </DialogDescription>
        </DialogHeader>

        {/* Order Information - Full Display */}
        <div className="space-y-6">
          {/* Order Summary Header */}
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-primary">
                  ออเดอร์ #{order.orderNumber}
                </h2>
                <p className="text-sm text-mute mt-1">
                  สร้างเมื่อ {fDate(order.createdAt)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary">
                  {fCurrency(order.finalAmount)}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {getStatusBadge(order.status)}
                  {getShippingStatusBadge(
                    order.shippingStatus || ShippingStatus.PENDING,
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="p-4 border rounded-lg">
            <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
              <User className="h-4 w-4" />
              ข้อมูลลูกค้า
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-mute">ชื่อ-นามสกุล</p>
                <p className="text-base text-black font-medium truncate">
                  {order.customer.displayName ||
                    `${order.customer.firstName} ${order.customer.lastName}` ||
                    '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-mute">เบอร์โทรศัพท์</p>
                <p className="text-base text-black">
                  {order.customer?.phoneNumber || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-mute">อีเมล</p>
                <p className="text-base text-black">
                  {order.customer?.email || '-'}
                </p>
              </div>

              <div className="md:col-span-2">
                <p className="text-sm text-mute">รหัสลูกค้า</p>
                <p className="text-base text-black font-mono">
                  {order.customer?.memberCode || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="p-4 border rounded-lg">
            <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              ที่อยู่จัดส่ง
            </h3>
            <div className="space-y-2">
              {order.userAddress ? (
                <p className="text-base text-black">
                  {order.userAddress.address}
                </p>
              ) : (
                <p className="text-base text-mute">ไม่มีข้อมูลที่อยู่</p>
              )}
            </div>
          </div>

          {/* Order Items */}
          <div className="p-4 border rounded-lg">
            <h3 className="text-base font-semibold mb-3">
              รายการสินค้า ({order.items?.length || 0} รายการ)
            </h3>
            <div className="space-y-4">
              {order.items?.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-3 border rounded-lg bg-gray-50"
                >
                  {/* Product Image */}
                  <div className="flex-shrink-0">
                    {item.product ? (
                      <img
                        src={getFirstImageMedia(item.product.mediaUrls) || ''}
                        alt={item.product.name}
                        className="w-16 h-16 object-cover rounded-md border"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          target.nextElementSibling?.classList.remove('hidden')
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-16 h-16 bg-gray-200 rounded-md flex items-center justify-center border ${item.product ? 'hidden' : ''}`}
                    >
                      <Package className="h-6 w-6 text-gray-400" />
                    </div>
                  </div>

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4
                          className="font-medium text-black truncate"
                          title={item.product?.name || ''}
                        >
                          {item.product?.name || 'ไม่ระบุชื่อสินค้า'}
                        </h4>
                        <div className="flex items-center gap-4 mt-1 text-sm text-mute">
                          <span>จำนวน: {item.quantity}</span>

                          {item.discount && item.discount > 0 ? (
                            <span className="text-green-600 font-medium">
                              ส่วนลด: {fCurrency(item.discount)}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {/* Price Information */}
                      <div className="text-right ml-4">
                        <div className="space-y-1">
                          <p className="text-sm text-mute">
                            ราคาต่อชิ้น: {fCurrency(item.price)}
                          </p>
                          <p className="font-semibold text-primary">
                            รวม: {fCurrency(item.price * item.quantity)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Order Summary */}
              <div className="mt-4 p-3 bg-white border rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-mute">ราคารวม:</span>
                    <span className="text-sm">
                      {fCurrency(order.totalAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-mute">ส่วนลด:</span>
                    <span className="text-sm">
                      -{fCurrency(order.discount || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-mute">ส่วนลดคูปอง:</span>
                    <span className="text-sm">
                      -{fCurrency(order.userCouponDiscount || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-mute">ค่าจัดส่ง:</span>
                    <span className="text-sm">
                      {fCurrency(order.shippingFee || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-semibold">ยอดรวมสุทธิ:</span>
                    <span className="font-semibold text-primary text-lg">
                      {fCurrency(order.finalAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="p-4 border rounded-lg">
            <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              ข้อมูลการชำระเงิน
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-mute">สถานะการชำระเงิน</p>
                {getStatusBadge(order.status)}
              </div>
              <div>
                <p className="text-sm text-mute">ยอดที่ต้องชำระ</p>
                <p className="text-lg font-semibold text-primary">
                  {fCurrency(order.finalAmount)}
                </p>
              </div>
              <div>
                <p className="text-sm text-mute">วิธีการชำระเงิน</p>
                <p className="text-base text-black font-medium">
                  {getBankLabel(order.bankName)}
                </p>
              </div>
              <div>
                <p className="text-sm text-mute">ชื่อบัญชี</p>
                <p className="text-base text-black">
                  {order.bankAccountName || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-mute">เลขบัญชี</p>
                <p className="text-base text-black font-mono">
                  {order.bankAccountNumber || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-mute">วันที่ยืนยันการชำระเงิน</p>
                <p className="text-base text-black">
                  {order.confirmedAt ? fDate(order.confirmedAt) : '-'}
                </p>
              </div>

              <div className="md:col-span-2">
                <p className="text-sm text-mute mb-2">หลักฐานการชำระเงิน</p>
                <div className="relative">
                  {order.paymentProof ? (
                    <img
                      src={order.paymentProof}
                      alt="หลักฐานการชำระเงิน"
                      className="w-full max-w-md h-auto rounded-lg border shadow-sm cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setLightboxOpen(true)}
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement
                        target.onerror = null
                        target.src = ''
                        target.classList.add('hidden')
                        const fallback =
                          target.nextElementSibling as HTMLElement | null
                        if (fallback) {
                          fallback.classList.remove('hidden')
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full max-w-md h-48 bg-gray-100 rounded-lg border flex items-center justify-center">
                      <p className="text-sm text-gray-500">ไม่มีรูปภาพ</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Order Notes */}
          {order.note && (
            <div className="p-4 border rounded-lg">
              <h3 className="text-base font-semibold mb-3">
                หมายเหตุเพิ่มเติม
              </h3>
              <div className="space-y-3">
                {order.note && (
                  <div>
                    <p className="text-sm text-mute mb-1">หมายเหตุออเดอร์:</p>
                    <p className="text-sm text-black bg-gray-50 p-2 rounded">
                      {order.note}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Shipping Status Update Form */}
          <div className="p-4 border rounded-lg bg-blue-50">
            <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              ยืนยันการชำระเงิน
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="notes"
                  className="text-base font-medium text-black"
                >
                  หมายเหตุ
                </Label>
                <Textarea
                  id=""
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="เพิ่มหมายเหตุสำหรับการอัปเดตสถานะการชำระเงิน (ไม่บังคับ)"
                  rows={3}
                />
              </div>
            </form>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            ปิด
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting}
            loading={submitting}
          >
            {submitting ? 'กำลังอัปเดต...' : 'อัปเดต'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Lightbox for payment proof image */}
      {order.paymentProof && (
        <LightboxDialog
          isOpen={lightboxOpen}
          onOpenChange={setLightboxOpen}
          mediaItems={[
            {
              type: 'image',
              url: order.paymentProof,
            },
          ]}
          initialIndex={0}
        />
      )}
    </Dialog>
  )
}
